import React, { useState, useRef, useEffect } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

interface CustomDatePickerProps {
  value: string; // YYYY-MM-DD format internally
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Convert YYYY-MM-DD to MM/DD/YYYY for display
const formatForDisplay = (dateStr: string): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return '';
  return `${month}/${day}/${year}`;
};

// Convert MM/DD/YYYY to YYYY-MM-DD for internal use
const formatForInternal = (dateStr: string): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('/');
  if (parts.length !== 3) return '';
  const [month, day, year] = parts;
  if (!month || !day || !year) return '';
  return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

// Validate if a string is a valid MM/DD/YYYY date
const isValidDateFormat = (dateStr: string): boolean => {
  const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
  if (!regex.test(dateStr)) return false;
  
  const [month, day, year] = dateStr.split('/').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getMonth() === month - 1 && date.getDate() === day && date.getFullYear() === year;
};

// Parse YYYY-MM-DD to Date object
const parseInternalDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  min,
  max,
  placeholder = 'MM/DD/YYYY',
  disabled = false,
  style = {}
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [inputValue, setInputValue] = useState(formatForDisplay(value));
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = parseInternalDate(value) || new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });
  const containerRef = useRef<HTMLDivElement>(null);

  // Update input value when value prop changes
  useEffect(() => {
    setInputValue(formatForDisplay(value));
  }, [value]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    
    // Auto-format as user types
    const digitsOnly = newValue.replace(/\D/g, '');
    
    if (digitsOnly.length <= 2) {
      newValue = digitsOnly;
    } else if (digitsOnly.length <= 4) {
      newValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
    } else {
      newValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4, 8)}`;
    }
    
    setInputValue(newValue);
    
    // Only update parent if valid complete date
    if (isValidDateFormat(newValue)) {
      const internalFormat = formatForInternal(newValue);
      
      // Check min/max constraints
      const selectedDate = parseInternalDate(internalFormat);
      const minDate = min ? parseInternalDate(min) : null;
      const maxDate = max ? parseInternalDate(max) : null;
      
      if (selectedDate) {
        if (minDate && selectedDate < minDate) return;
        if (maxDate && selectedDate > maxDate) return;
        onChange(internalFormat);
        setCurrentMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
      }
    }
  };

  const handleInputBlur = () => {
    // Reset to valid value on blur if invalid
    if (!isValidDateFormat(inputValue)) {
      setInputValue(formatForDisplay(value));
    }
  };

  const handleDateSelect = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const internalFormat = `${year}-${month}-${day}`;
    
    onChange(internalFormat);
    setInputValue(formatForDisplay(internalFormat));
    setShowCalendar(false);
  };

  const navigateMonth = (direction: number) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev.getFullYear(), prev.getMonth() + direction, 1);
      return newMonth;
    });
  };

  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateDisabled = (date: Date): boolean => {
    const minDate = min ? parseInternalDate(min) : null;
    const maxDate = max ? parseInternalDate(max) : null;
    
    if (minDate) {
      minDate.setHours(0, 0, 0, 0);
      if (date < minDate) return true;
    }
    if (maxDate) {
      maxDate.setHours(23, 59, 59, 999);
      if (date > maxDate) return true;
    }
    return false;
  };

  const isDateSelected = (date: Date): boolean => {
    const selectedDate = parseInternalDate(value);
    if (!selectedDate) return false;
    return (
      date.getFullYear() === selectedDate.getFullYear() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getDate() === selectedDate.getDate()
    );
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days: React.ReactNode[] = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty" />);
    }

    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const disabled = isDateDisabled(date);
      const selected = isDateSelected(date);
      const today = isToday(date);

      days.push(
        <div
          key={day}
          className={`calendar-day ${disabled ? 'disabled' : ''} ${selected ? 'selected' : ''} ${today ? 'today' : ''}`}
          onClick={() => !disabled && handleDateSelect(date)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  return (
    <div 
      ref={containerRef} 
      className="custom-date-picker"
      style={{ position: 'relative', ...style }}
    >
      <div className="date-input-wrapper" style={{ position: 'relative' }}>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={() => setShowCalendar(true)}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={10}
          className="date-picker-input"
        />
        <FiCalendar
          className="date-picker-icon"
          onClick={() => !disabled && setShowCalendar(!showCalendar)}
          style={{
            pointerEvents: disabled ? 'none' : 'auto'
          }}
        />
      </div>

      {showCalendar && (
        <div className="calendar-dropdown">
          {/* Header */}
          <div className="calendar-header">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="calendar-nav-btn"
            >
              <FiChevronLeft />
            </button>
            <span className="calendar-month-year">
              {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </span>
            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="calendar-nav-btn"
            >
              <FiChevronRight />
            </button>
          </div>

          {/* Days of week header */}
          <div className="calendar-weekdays">
            {DAYS_OF_WEEK.map(day => (
              <div key={day} className="calendar-weekday">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="calendar-grid">
            {renderCalendar()}
          </div>

          {/* Today button */}
          <div className="calendar-footer">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                if (!isDateDisabled(today)) {
                  handleDateSelect(today);
                }
                setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
              }}
              className="today-btn"
            >
              Today
            </button>
          </div>
        </div>
      )}

      <style>{`
        .custom-date-picker .date-picker-input {
          width: 100%;
          padding: 8px 36px 8px 12px;
          borderRadius: 6px;
          border: 1px solid var(--border-color);
          fontSize: 14px;
          background-color: var(--bg-input);
          color: var(--text-primary);
          cursor: text;
          transition: all 0.2s;
        }
        .custom-date-picker .date-picker-input:disabled {
          background-color: var(--bg-code);
          color: var(--text-disabled);
          cursor: not-allowed;
        }
        .custom-date-picker .date-picker-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .custom-date-picker .date-picker-input::placeholder {
          color: var(--text-muted);
        }
        .custom-date-picker .date-picker-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
          cursor: pointer;
          transition: color 0.2s;
        }
        .custom-date-picker .date-picker-icon:hover {
          color: var(--text-primary);
        }
        .custom-date-picker .calendar-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 4px;
          padding: 12px;
          background: var(--bg-card);
          border-radius: 8px;
          box-shadow: 0 10px 25px var(--shadow);
          border: 1px solid var(--border-color);
          z-index: 1000;
          min-width: 280px;
        }
        .custom-date-picker .calendar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
          padding: 0 4px;
        }
        .custom-date-picker .calendar-nav-btn {
          padding: 4px 8px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          color: var(--text-primary);
          transition: all 0.2s;
        }
        .custom-date-picker .calendar-nav-btn:hover {
          background: var(--bg-code);
          border-color: #667eea;
        }
        .custom-date-picker .calendar-month-year {
          font-weight: 600;
          font-size: 14px;
          color: var(--text-primary);
        }
        .custom-date-picker .calendar-weekdays {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          margin-bottom: 4px;
        }
        .custom-date-picker .calendar-weekday {
          text-align: center;
          font-size: 11px;
          font-weight: 600;
          color: var(--text-muted);
          padding: 4px;
        }
        .custom-date-picker .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
        }
        .custom-date-picker .calendar-day {
          text-align: center;
          padding: 8px 4px;
          font-size: 13px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.15s;
          color: var(--text-primary);
        }
        .custom-date-picker .calendar-day:not(.empty):not(.disabled):hover {
          background: var(--bg-code);
        }
        .custom-date-picker .calendar-day.empty {
          cursor: default;
        }
        .custom-date-picker .calendar-day.disabled {
          color: var(--text-disabled);
          cursor: not-allowed;
        }
        .custom-date-picker .calendar-day.selected {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        .custom-date-picker .calendar-day.today:not(.selected) {
          border: 1px solid #667eea;
          color: #667eea;
          font-weight: 600;
        }
        .custom-date-picker .calendar-footer {
          margin-top: 8px;
          text-align: center;
        }
        .custom-date-picker .today-btn {
          padding: 6px 12px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          color: var(--text-primary);
          transition: all 0.2s;
        }
        .custom-date-picker .today-btn:hover {
          background: var(--bg-code);
          border-color: #667eea;
        }
      `}</style>
    </div>
  );
};

export default CustomDatePicker;
